import { Helmet } from 'react-helmet-async';
import ContactForm from '../contactForm/ContactForm';
import styles from './ServicePage.module.css';

const CALENDAR_URL = 'https://calendar.app.google/4YtwnKduScqcFVjc9';

const ServicePage = ({ serviceData }) => {
  const {
    title,
    metaDescription,
    keywords,
    hero,
    whyChooseUs,
    process,
    technologies,
    results,
    architecture,
    integrations,
    caseStudies,
    faq,
    schema
  } = serviceData;

  const canonicalUrl = `https://www.danielrayrangel.com/services/${serviceData.slug}`;
  const ogImage = serviceData.ogImage || 'https://www.danielrayrangel.com/Exhale.Daniel.Ray.Rangel.png';

  // Build the "On this page" nav from sections that actually exist.
  const toc = [
    { id: 'overview', label: 'Overview' },
    whyChooseUs && { id: 'capabilities', label: 'Capabilities' },
    architecture && { id: 'architecture', label: 'Architecture' },
    integrations && { id: 'integrations', label: 'Integrations' },
    caseStudies && { id: 'work', label: 'Selected work' },
    process && { id: 'process', label: 'Process' },
    results && { id: 'numbers', label: 'By the numbers' },
    technologies && { id: 'stack', label: 'Stack' },
    faq && { id: 'faq', label: 'FAQ' },
    { id: 'contact', label: 'Get in touch' }
  ].filter(Boolean);

  return (
    <>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={metaDescription} />
        <meta name="keywords" content={keywords.join(', ')} />
        <link rel="canonical" href={canonicalUrl} />

        <meta property="og:title" content={title} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:image" content={ogImage} />
        <meta property="og:site_name" content="Exhale Software Solutions" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={metaDescription} />
        <meta name="twitter:image" content={ogImage} />

        <script type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      </Helmet>

      <div className={styles.docs}>
        <div className={styles.shell}>

          <main className={styles.main}>
            {/* ── Header ── */}
            <nav className={styles.breadcrumb} aria-label="Breadcrumb">
              <a href="/">home</a>
              <span className={styles.crumbSep}>/</span>
              <span>services</span>
              <span className={styles.crumbSep}>/</span>
              <span className={styles.crumbCurrent}>{hero.kicker ? hero.kicker.toLowerCase().replace(/\s+/g, '-') : 'service'}</span>
            </nav>

            <span className={styles.eyebrow}>{hero.kicker || 'Service'}</span>
            <h1 className={styles.docTitle}>
              {hero.title}{hero.accent ? ` ${hero.accent}` : ''}
            </h1>
            <p className={styles.docLead}>{hero.subtitle}</p>

            <div className={styles.statusRow}>
              <span className={styles.badge}><span className={styles.dot} /> Available for work</span>
              <span className={styles.badgeMuted}>Phoenix, AZ · Remote</span>
              <a href={CALENDAR_URL} target="_blank" rel="noopener noreferrer" className={styles.headerCta}>Book consultation →</a>
            </div>

            {/* ── Overview ── */}
            <section id="overview" className={styles.section}>
              <h2 className={styles.h2}>Overview</h2>
              {hero.lede && <p className={styles.prose}>{hero.lede}</p>}
            </section>

            {/* ── Capabilities ── */}
            {whyChooseUs && whyChooseUs.benefits && (
              <section id="capabilities" className={styles.section}>
                <h2 className={styles.h2}>Capabilities</h2>
                <dl className={styles.defList}>
                  {whyChooseUs.benefits.map((b, i) => (
                    <div key={i} className={styles.defRow}>
                      <dt className={styles.defTerm}>{b.title}</dt>
                      <dd className={styles.defDesc}>{b.description}</dd>
                    </div>
                  ))}
                </dl>
              </section>
            )}

            {/* ── Architecture exhibit ── */}
            {architecture && architecture.flow && (
              <section id="architecture" className={styles.section}>
                <h2 className={styles.h2}>{architecture.title || 'Architecture'}</h2>
                {architecture.caption && <p className={styles.prose}>{architecture.caption}</p>}
                <figure className={styles.exhibit}>
                  <div className={styles.exhibitBar}>
                    <span className={styles.exhibitDots} aria-hidden="true"><i /><i /><i /></span>
                    <span className={styles.exhibitName}>forecasting-engine</span>
                    <span className={styles.exhibitTag}>production</span>
                  </div>
                  <div className={styles.exhibitBody}>
                    {architecture.flow.map((stage, i) => (
                      <div key={i} className={styles.flowStage}>
                        <div className={styles.flowNode}>
                          <span className={styles.flowLabel}>{stage.label}</span>
                          <ul className={styles.flowList}>
                            {stage.items.map((it, j) => <li key={j}>{it}</li>)}
                          </ul>
                        </div>
                        {i < architecture.flow.length - 1 && (
                          <span className={styles.flowArrow} aria-hidden="true">→</span>
                        )}
                      </div>
                    ))}
                  </div>
                </figure>
              </section>
            )}

            {/* ── Integrations ── */}
            {integrations && integrations.items && (
              <section id="integrations" className={styles.section}>
                <h2 className={styles.h2}>{integrations.title || 'Integrations'}</h2>
                {integrations.caption && <p className={styles.prose}>{integrations.caption}</p>}
                <div className={styles.integ}>
                  <div className={styles.integHub}>Salesforce</div>
                  <span className={styles.integLink} aria-hidden="true">⇄</span>
                  <ul className={styles.integGrid}>
                    {integrations.items.map((it, i) => (
                      <li key={i} className={styles.integCell}>
                        <span className={styles.integName}>{it.name}</span>
                        {it.method && <span className={styles.integMethod}>{it.method}</span>}
                      </li>
                    ))}
                  </ul>
                </div>
              </section>
            )}

            {/* ── Selected work ── */}
            {caseStudies && caseStudies.items && (
              <section id="work" className={styles.section}>
                <h2 className={styles.h2}>{caseStudies.title || 'Selected work'}</h2>
                <div className={styles.cases}>
                  {caseStudies.items.map((cs, i) => (
                    <article key={i} className={styles.case}>
                      <div className={styles.caseHead}>
                        <h3 className={styles.caseTitle}>{cs.title}</h3>
                        {cs.context && <span className={styles.caseChip}>{cs.context}</span>}
                      </div>
                      <p className={styles.caseRow}><span className={styles.caseKey}>challenge</span>{cs.problem}</p>
                      <p className={styles.caseRow}><span className={styles.caseKey}>built</span>{cs.solution}</p>
                      <p className={styles.caseRow}><span className={styles.caseKey}>result</span>{cs.result}</p>
                    </article>
                  ))}
                </div>
              </section>
            )}

            {/* ── Process ── */}
            {process && process.steps && (
              <section id="process" className={styles.section}>
                <h2 className={styles.h2}>{process.title || 'Process'}</h2>
                <ol className={styles.steps}>
                  {process.steps.map((s, i) => (
                    <li key={i} className={styles.step}>
                      <span className={styles.stepNo}>{String(i + 1).padStart(2, '0')}</span>
                      <div>
                        <h3 className={styles.stepTitle}>{s.title}</h3>
                        <p className={styles.stepDesc}>{s.description}</p>
                      </div>
                    </li>
                  ))}
                </ol>
              </section>
            )}

            {/* ── By the numbers ── */}
            {results && results.items && (
              <section id="numbers" className={styles.section}>
                <h2 className={styles.h2}>{results.title || 'By the numbers'}</h2>
                <div className={styles.stats}>
                  {results.items.map((r, i) => (
                    <div key={i} className={styles.stat}>
                      <span className={styles.statMetric}>{r.metric}</span>
                      <span className={styles.statDesc}>{r.description}</span>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* ── Stack ── */}
            {technologies && technologies.items && (
              <section id="stack" className={styles.section}>
                <h2 className={styles.h2}>{technologies.title || 'Stack'}</h2>
                <ul className={styles.chips}>
                  {technologies.items.map((t, i) => <li key={i} className={styles.chip}>{t}</li>)}
                </ul>
              </section>
            )}

            {/* ── FAQ ── */}
            {faq && faq.items && (
              <section id="faq" className={styles.section}>
                <h2 className={styles.h2}>{faq.title || 'FAQ'}</h2>
                <div className={styles.faq}>
                  {faq.items.map((item, i) => (
                    <div key={i} className={styles.faqItem}>
                      <h3 className={styles.faqQ}>{item.question}</h3>
                      <p className={styles.faqA}>{item.answer}</p>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* ── Contact ── */}
            <section id="contact" className={styles.section}>
              <h2 className={styles.h2}>Get in touch</h2>
              <div className={styles.callout}>
                <div className={styles.calloutText}>
                  <p className={styles.prose}>
                    A free 30-minute consultation. We'll look at your org and decide if there's a fit — no strings attached.
                  </p>
                  <p className={styles.contactLinks}>
                    <a href="mailto:daniel@danielrayrangel.com">daniel@danielrayrangel.com</a>
                    <span className={styles.crumbSep}>·</span>
                    <a href="tel:+14802820569">(480) 282-0569</a>
                    <span className={styles.crumbSep}>·</span>
                    <a href="https://www.linkedin.com/in/danielrayrangel/" target="_blank" rel="noopener noreferrer">LinkedIn</a>
                  </p>
                </div>
                <div className={styles.calloutForm}>
                  <ContactForm />
                </div>
              </div>
            </section>
          </main>

          {/* ── On this page ── */}
          <aside className={styles.toc} aria-label="On this page">
            <p className={styles.tocLabel}>On this page</p>
            <ul className={styles.tocList}>
              {toc.map((t) => (
                <li key={t.id}><a href={`#${t.id}`} className={styles.tocLink}>{t.label}</a></li>
              ))}
            </ul>
          </aside>

        </div>
      </div>
    </>
  );
};

export default ServicePage;
